import {
  ChevronDownIcon,
  LinkExternal01Icon,
} from '@langchain/untitled-ui-icons';
import { Option, Select } from '@mui/joy';

import { CodeDataEditor } from '@/components/Code/Code';
import { Button, Tooltip } from '@/design-system';
import { BuiltinTool, builtinToolsForModelId } from '@/utils/ai-tools';

import { BUILTIN_TOOLS } from '../../Playground.constants';

export const BuiltinToolEditor = ({
  modelId,
  tool,
  setTool,
  onClose,
  onSave,
  saveDisabled,
  saveTooltipText,
  setShowRemoveConfirmation,
  isEditingExisting,
  setIsStreaming,
}: {
  modelId: string;
  tool: BuiltinTool;
  setTool: (tool: BuiltinTool) => void;
  onClose: () => void;
  onSave: (builtinTool: BuiltinTool) => void;
  saveDisabled: boolean;
  saveTooltipText?: string;
  setShowRemoveConfirmation?: (showRemoveConfirmation: boolean) => void;
  isEditingExisting: boolean;
  setIsStreaming: (isStreaming: boolean) => void;
}) => {
  const builtinTools = builtinToolsForModelId(modelId);

  const currentTool = BUILTIN_TOOLS[modelId].find(
    (t) => t.tool.type === tool.type
  );
  const displayName = currentTool?.displayName ?? tool.type;
  const description = currentTool?.description;
  const docsLink = currentTool?.docsLink;

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <div className="flex flex-col gap-1">
        {isEditingExisting ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 pb-2 text-lg font-semibold">
              Edit tool
            </div>
            <div className="flex flex-row items-center gap-1 text-sm font-medium">
              {displayName}
            </div>
          </div>
        ) : (
          <Select
            value={tool.type}
            onChange={(_, value) => {
              if (currentTool?.disableStreaming) {
                setIsStreaming(false);
              }
              const selectedTool = builtinTools.find(
                (t) => t.tool.type === value
              )?.tool;
              if (selectedTool) {
                setTool(selectedTool);
              }
            }}
            indicator={<ChevronDownIcon className="h-4 w-4" />}
          >
            {builtinTools.map((t, idx) => (
              <Option key={idx} value={t.tool.type}>
                {t.displayName}
              </Option>
            ))}
            {!builtinTools.some((t) => t.tool.type === tool.type) &&
              tool.type && <Option value={tool.type}>{tool.type}</Option>}
          </Select>
        )}
        {(description || docsLink) && (
          <div className="flex flex-row items-center gap-1 text-sm text-secondary">
            {description && <span>{description}</span>}
            {docsLink && (
              <a href={docsLink} target="_blank" rel="noopener noreferrer">
                <LinkExternal01Icon className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-grow">
        <CodeDataEditor
          key={tool.type}
          value={tool}
          onChange={(value) => setTool(value)}
          language={'json'}
        />
      </div>

      <div className="flex flex-shrink-0 justify-end gap-2">
        {setShowRemoveConfirmation && (
          <Button
            data-testid="schema-modal-remove-button"
            color="error"
            variant="outlined"
            onClick={() => setShowRemoveConfirmation(true)}
          >
            Delete
          </Button>
        )}
        <div className="ml-auto flex flex-row gap-1">
          <Button
            data-testid="schema-modal-cancel-button"
            color="primary"
            variant="plain"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Tooltip title={saveTooltipText}>
            <div>
              <Button
                data-testid="schema-modal-save-button"
                color="primary"
                onClick={() => onSave(tool)}
                disabled={saveDisabled}
              >
                Save changes
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
